import type { Dispatch, FC, SetStateAction } from 'react'
import { useMemo, useRef, useState } from 'react'
import {
  CwsButton,
  CwsDivider,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsH4,
  CwsIcon,
  CwsModal,
  CwsModalBody,
  CwsModalFooter,
  CwsP,
  CwsSelect,
  CwsSelectOption,
} from '@components/cws'
import { useWishlist } from '@hooks/useWishlist'
import { useTranslate } from '@hooks/useTranslation'
import { useRouter } from 'next/router'
import Link from 'next/link'

// @ts-ignore
import { modal } from 'cms-web-styleguide/dist/utils/utils'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import { useMediaLess } from '@ui/hooks/useMediaLess'
import { device600 } from '@ui/hooks/useResize'

const options = [
  {
    value: 'a-z',
    title: 'search.page.orderby.nameasc',
  },
  {
    value: 'z-a',
    title: 'search.page.orderby.namedesc',
  },
  {
    value: 'price-ascending',
    title: 'search.page.orderby.priceasc',
  },
  {
    value: 'price-descending',
    title: 'search.page.orderby.pricedesc',
  },
]

interface WishlistOptionsProps {
  changeSortBy: (value: string) => void
  sortBy: string
  setSendEmailVisible: Dispatch<SetStateAction<boolean>>
}

export const WL_MODAL_ID = 'clear-wishlist-modal'

export const WishlistOptions: FC<WishlistOptionsProps> = ({ changeSortBy, sortBy, setSendEmailVisible }) => {
  const { clearList } = useWishlist()
  const { translate } = useTranslate()
  const [isClearList, setIsClearList] = useState(false)

  const {
    query: { catalogId },
  } = useRouter()

  const handlerClearList = async () => {
    await clearList()
    setIsClearList(false)
    modal.addEventModalHide(WL_MODAL_ID)
  }
  const refOption = useRef(null)

  const printHref = useMemo(() => {
    if (isGloballyPricelist() && catalogId) {
      return `/${catalogId}/wishlist-print`
    }

    return '/wishlist-print'
  }, [catalogId])

  const isMobile = useMediaLess(device600)

  return (
    <div ref={refOption}>
      <CwsDivider spacing="s" />
      <CwsGridRow>
        <CwsGridCol
          xs="6"
          md="5"
          lg="3"
          className="flex flex-col justify-start items-start gap-2 lg:flex-row lg:items-baseline"
        >
          <CwsP size="small" className="whitespace-nowrap">
            {translate('search.page.orderby.label')}:
          </CwsP>
          <CwsSelect value={sortBy} onChange={changeSortBy}>
            {options.map(({ title, value }) => (
              <CwsSelectOption key={value} value={value} title={translate(title)} />
            ))}
          </CwsSelect>
        </CwsGridCol>

        <CwsGridCol xs="6" md="7" lg="9" className="flex justify-end items-end gap-6 lg:items-center">
          {!isMobile && (
            <Link href={printHref} target="__blank">
              <CwsButton variant="secondary" className="h-10">
                <div className="flex items-center gap-2">
                  <CwsIcon icon="printer" size="20px" />
                  <CwsP size="small" hideSpacing className="hidden lg:block capitalize">
                    {translate('cart.confirmation.btn.print')}
                  </CwsP>
                </div>
              </CwsButton>
            </Link>
          )}

          <CwsButton
            data-modal-id="send-email-form"
            variant="secondary"
            className="h-10"
            onClick={() => setSendEmailVisible(true)}
          >
            <div className="flex items-center gap-2 ">
              <CwsIcon icon="email" size="20px" />
              <CwsP size="small" hideSpacing className="hidden lg:block capitalize">
                {translate('product.detail.share.email')}
              </CwsP>
            </div>
          </CwsButton>
          <div className="h-full max-h-10">
            <CwsDivider orientation="vertical" />
          </div>
          <CwsButton
            data-modal-id={WL_MODAL_ID}
            variant="secondary"
            className="h-10"
            onClick={() => setIsClearList(true)}
          >
            <div className="flex items-center gap-2 ">
              <CwsIcon icon="bin" size="20px" />
              <CwsP size="small" hideSpacing className="hidden lg:block capitalize">
                {translate('w2.wishlist.list.clear')}
              </CwsP>
            </div>
          </CwsButton>
        </CwsGridCol>
      </CwsGridRow>
      {!isMobile && <CwsDivider spacing="s" />}
      <CwsModal id={WL_MODAL_ID} hideCloseButton visible={isClearList} onClose={() => setIsClearList(false)}>
        <CwsModalBody className="max-w-3xl">
          <CwsGrid width="fluid">
            <CwsGridRow>
              <CwsGridCol>
                <CwsH4>{translate('w2.wishlist.list.clear')}</CwsH4>
                <CwsP>{translate('w2.wishlist.list.clear.message')}</CwsP>
              </CwsGridCol>
            </CwsGridRow>
          </CwsGrid>
        </CwsModalBody>
        <CwsModalFooter>
          <CwsGrid width="fluid">
            <CwsGridRow justifyContent="space-between">
              <CwsGridCol col="12" sm="6" className="text-center sm:text-left">
                <CwsButton
                  variant="empty"
                  width="fluid-mobile"
                  onClick={() => {
                    setIsClearList(false)
                    modal.addEventModalHide(WL_MODAL_ID)
                  }}
                >
                  {translate('profile.details.cancel')}
                </CwsButton>
              </CwsGridCol>
              <CwsGridCol col="12" sm="6" className="flex justify-center sm:justify-end">
                <CwsButton variant="cancel" onClick={handlerClearList} width="fluid-mobile">
                  {translate('w2.wishlist.list.clear')}
                </CwsButton>
              </CwsGridCol>
            </CwsGridRow>
          </CwsGrid>
        </CwsModalFooter>
      </CwsModal>
    </div>
  )
}
